import React, { createContext, useCallback, useEffect, useState } from 'react';
import { TownConfig } from '../types/config';
import defaultConfig from '../config/townConfig.json';
import { useDashboardConfig } from '../hooks/useDashboardConfig';

interface ConfigContextType {
  config: TownConfig;
  updateConfig: (newConfig: Partial<TownConfig>) => void;
  saveNewDashboard: (name: string, config: TownConfig) => string;
}

export const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({
  children,
  dashboardId,
}: {
  children: React.ReactNode;
  dashboardId?: string;
}) {
  const { getConfig, updateConfig: updateStoredConfig, saveConfig } = useDashboardConfig();
  const [config, setConfig] = useState<TownConfig>(defaultConfig as TownConfig);

  useEffect(() => {
    if (!dashboardId) {
      setConfig(defaultConfig as TownConfig);
      return;
    }

    const storedConfig = getConfig(dashboardId);
    if (storedConfig) {
      setConfig(storedConfig.config);
      return;
    }

    setConfig(defaultConfig as TownConfig);
  }, [dashboardId, getConfig]);

  const updateConfig = useCallback((newConfig: Partial<TownConfig>) => {
    setConfig(current => {
      const updated = {
        ...current,
        ...newConfig,
        data: {
          ...current.data,
          ...(newConfig.data || {})
        }
      };
      
      if (dashboardId) {
        updateStoredConfig(dashboardId, updated);
      }
      return updated;
    });
  }, [dashboardId, updateStoredConfig]);

  const saveNewDashboard = useCallback((name: string, dashboardConfig: TownConfig) => {
    return saveConfig(name, dashboardConfig);
  }, [saveConfig]);

  return (
    <ConfigContext.Provider value={{ config, updateConfig, saveNewDashboard }}>
      {children}
    </ConfigContext.Provider>
  );
}
